import { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { logger } from '../../utils/logger';
import { reapplyWatermarks } from '../../utils/processingApi';

interface ImageProtectionSectionProps {
  watermarkEnabled: boolean;
  onWatermarkChange: (checked: boolean) => Promise<void>;
}

export const ImageProtectionSection = ({
  watermarkEnabled,
  onWatermarkChange,
}: ImageProtectionSectionProps) => {
  const [reapplyDialogOpen, setReapplyDialogOpen] = useState(false);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const handleReapply = async () => {
    setReapplyLoading(true);
    setReapplyDialogOpen(false);
    try {
      const result = await reapplyWatermarks();
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.queued > 0 ? 'success' : 'info',
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number; headers?: Record<string, string> } };
      if (axiosError.response?.status === 429) {
        const retryAfter = axiosError.response.headers?.['retry-after'];
        const minutes = retryAfter ? Math.ceil(parseInt(retryAfter, 10) / 60) : 60;
        setSnackbar({
          open: true,
          message: `Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before reapplying.`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to reapply watermarks. Please try again later.',
          severity: 'error',
        });
      }
      logger.error('Failed to reapply watermarks:', err);
    } finally {
      setReapplyLoading(false);
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={watermarkEnabled}
            onChange={async (e) => {
              try {
                await onWatermarkChange(e.target.checked);
              } catch (err) {
                logger.error('Failed to update watermark preference:', err);
              }
            }}
          />
        }
        label="Add watermark to public images"
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
        Adds a copyright watermark with your name to newly uploaded images to deter unauthorized use.
        Use the button below to apply changes to existing images.
      </Typography>
      <Box sx={{ ml: 4, mt: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={reapplyLoading}
          onClick={() => setReapplyDialogOpen(true)}
          startIcon={reapplyLoading ? <CircularProgress size={16} /> : undefined}
        >
          {reapplyLoading ? 'Reprocessing...' : 'Reapply Watermarks to Existing Images'}
        </Button>
      </Box>

      {/* Reapply Watermarks Confirmation Dialog */}
      <Dialog
        open={reapplyDialogOpen}
        onClose={() => !reapplyLoading && setReapplyDialogOpen(false)}
      >
        <DialogTitle>Reapply Watermarks</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reprocess all your existing images with your current watermark settings.
            This may take several minutes depending on how many images you have.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReapplyDialogOpen(false)} disabled={reapplyLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReapply}
            variant="contained"
            disabled={reapplyLoading}
            autoFocus
          >
            Reapply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
